// Search results (the prototype's top-bar "Search quotes, clients or calls..."), grouped
// into quotes, quote requests and phone calls. Quote visibility is applied by the API.
import { Link, useSearchParams } from 'react-router';
import { Banner, useQuery } from '@acc/web-shared';
import type { CallStatus, LifeStatus, PcStatus, QuoteType, Routing } from '@acc/domain';
import { PageHead, shortDate, useAgency } from '../agency';
import { CallPill, QuotePill } from './pills';

interface Results {
  q: string;
  quotes: { total: number; items: Array<{ opportunityId: string; customerName: string; phone: string | null; type: QuoteType; pcStatus: PcStatus | null; lifeStatus: LifeStatus | null; ownerMembershipId: string | null; dueDate: string | null; routing: Routing }> };
  intake: { total: number; items: Array<{ intakeId: string; customerName: string; phone: string | null; type: QuoteType; stage: 'Unassigned' | 'Assigned'; opportunityId: string | null; submittedAt: string }> };
  calls: { total: number; items: Array<{ callId: string; customerName: string; phone: string; reason: string; status: CallStatus; ownerMembershipId: string | null; callDate: string }> };
}

const more = (total: number, shown: number) => (total > shown ? <p className="muted">Showing the {shown} most recent of {total}. Add more words to narrow the search.</p> : null);

export function Search() {
  const a = useAgency();
  const [params] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const r = useQuery<Results>(q.length >= 2 ? a.path(`/search?q=${encodeURIComponent(q)}`) : null);
  if (q.length < 2) return <><PageHead title="Search" /><div className="card empty">Type at least two letters or digits in the search box above.</div></>;
  if (r.error) return <><PageHead title="Search" /><Banner tone="bad">{r.error.message}</Banner></>;
  if (!r.data) return <p className="muted">Searching…</p>;
  const d = r.data;
  const found = d.quotes.total + d.intake.total + d.calls.total;
  return (
    <>
      <PageHead title="Search" sub={found ? `${found} result${found === 1 ? '' : 's'} for "${d.q}".` : `Nothing found for "${d.q}".`} />
      {d.quotes.total > 0 && (
        <section className="panel" aria-labelledby="s-quotes">
          <div className="panelhead"><h2 id="s-quotes">Quotes ({d.quotes.total})</h2></div>
          <table className="data">
            <thead><tr><th>Customer</th><th>Phone</th><th>Type</th><th>P/C Status</th><th>Life Status</th><th>Assigned To</th><th>Due Date</th></tr></thead>
            <tbody>
              {d.quotes.items.map((o) => (
                <tr key={o.opportunityId}>
                  <td><Link to={`${a.base}/quotes/${o.opportunityId}`}>{o.customerName}</Link></td>
                  <td>{o.phone ?? '—'}</td>
                  <td><QuotePill kind="type" value={o.type} /></td>
                  <td><QuotePill kind="pc" value={o.pcStatus} /></td>
                  <td><QuotePill kind="life" value={o.lifeStatus} /></td>
                  <td>{o.ownerMembershipId ? a.name(o.ownerMembershipId) : 'Unassigned'}</td>
                  <td>{shortDate(o.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {more(d.quotes.total, d.quotes.items.length)}
        </section>
      )}
      {d.intake.total > 0 && (
        <section className="panel" aria-labelledby="s-intake">
          <div className="panelhead"><h2 id="s-intake">Quote requests ({d.intake.total})</h2></div>
          <table className="data">
            <thead><tr><th>Customer</th><th>Phone</th><th>Type</th><th>Submitted</th><th>Stage</th></tr></thead>
            <tbody>
              {d.intake.items.map((i) => (
                <tr key={i.intakeId}>
                  <td>{i.stage === 'Unassigned' && a.manager ? <Link to={`${a.base}/intake/${i.intakeId}/assign`}>{i.customerName}</Link> : i.opportunityId ? <Link to={`${a.base}/quotes/${i.opportunityId}`}>{i.customerName}</Link> : i.customerName}</td>
                  <td>{i.phone ?? '—'}</td>
                  <td><QuotePill kind="type" value={i.type} /></td>
                  <td>{shortDate(i.submittedAt)}</td>
                  <td>{i.stage === 'Unassigned' ? 'Awaiting assignment' : 'Assigned'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {more(d.intake.total, d.intake.items.length)}
        </section>
      )}
      {d.calls.total > 0 && (
        <section className="panel" aria-labelledby="s-calls">
          <div className="panelhead"><h2 id="s-calls">Phone calls ({d.calls.total})</h2></div>
          <table className="data">
            <thead><tr><th>Customer</th><th>Phone</th><th>Reason</th><th>Owner</th><th>Date</th><th>Status</th></tr></thead>
            <tbody>
              {d.calls.items.map((c) => (
                <tr key={c.callId}>
                  <td><Link to={`${a.base}/calls/${c.callId}`}>{c.customerName}</Link></td>
                  <td>{c.phone}</td>
                  <td>{c.reason}</td>
                  <td>{c.ownerMembershipId ? a.name(c.ownerMembershipId) : '—'}</td>
                  <td>{shortDate(c.callDate)}</td>
                  <td><CallPill s={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {more(d.calls.total, d.calls.items.length)}
        </section>
      )}
    </>
  );
}
